<script>
	import { nanoid } from "nanoid";
	import { tick } from "svelte";

	import { TelInput, normalizedCountries } from "./TelInput";

	import { clickOutsideAction } from "./TelInput/utils";
	import "./TelInput/styles/flags.css";

	export let id = nanoid(6);
	export let name = null;
	export let label = null;
	export let sublabel = null;
	export let value;
	export let clickOutside = true;
	export let closeOnClick = true;
	export let required = false;
	export let disabled = false;
	export let detailedValue;
	export let searchPlaceholder = "Search";
	export let selectedCountry = "AU";
	export let valid;
	export let options;
	export let validator = null;
	export let validatorMessage = null;
	export let validatorTarget = "#" + id + "_error";
	export let onchange = () => {};

	let selectedCountryDialCode = null;

	let inputVaue = value;

	let searchInput = null;
	let searchText = "";
	let isOpen = false;

	$: selectedCountryDialCode = normalizedCountries.find((el) => el.iso2 === selectedCountry)?.dialCode || null;

	const toggleDropDown = async (e) => {
		e?.preventDefault();
		if (disabled) return;
		isOpen = !isOpen;
		if (isOpen) {
			console.log(searchInput);
			await tick();
			searchInput && searchInput.focus();
		}
	};

	const closeDropdown = (e) => {
		if (isOpen) {
			e?.preventDefault();
			isOpen = false;
			searchText = "";
		}
	};

	const selectClick = () => {
		if (closeOnClick) closeDropdown();
	};

	const closeOnClickOutside = () => {
		if (clickOutside) {
			closeDropdown();
		}
	};

	const sortCountries = (countries, text) => {
		const normalizedText = text.trim().toLowerCase();
		if (!normalizedText) {
			return countries.sort((a, b) => a.label.localeCompare(b.label));
		}
		return countries.sort((a, b) => {
			const aNameLower = a.name.toLowerCase();
			const bNameLower = b.name.toLowerCase();
			const aStartsWith = aNameLower.startsWith(normalizedText);
			const bStartsWith = bNameLower.startsWith(normalizedText);
			if (aStartsWith && !bStartsWith) return -1;
			if (!aStartsWith && bStartsWith) return 1;
			const aIndex = aNameLower.indexOf(normalizedText);
			const bIndex = bNameLower.indexOf(normalizedText);
			if (aIndex === -1 && bIndex === -1) return a.id.localeCompare(b.id);
			if (aIndex === -1) return 1;
			if (bIndex === -1) return -1;
			const aWeight = aIndex + (aStartsWith ? 0 : 1);
			const bWeight = bIndex + (bStartsWith ? 0 : 1);
			return aWeight - bWeight;
		});
	};

	const handleSelect = (val, e) => {
		if (disabled) return;
		e?.preventDefault();
		if (
			selectedCountry === undefined ||
			selectedCountry === null ||
			(typeof selectedCountry === typeof val && selectedCountry !== val)
		) {
			selectedCountry = val;
			onchange && onchange(val);
			selectClick();
		} else {
			selectClick();
		}
	};

	$: {
		value = detailedValue ? detailedValue.phoneNumber : null;
	}
</script>

{#if label}
	<label for={id}>
		{label}

		{#if required && !disabled}
			<span class="required">*</span>
		{/if}

		{#if sublabel}
			<div class="sublabel">{sublabel}</div>
		{/if}
	</label>
{/if}

<div>
	<div class="flex" use:clickOutsideAction={closeOnClickOutside}>
		<button
			{disabled}
			id="states-button"
			data-dropdown-toggle="dropdown-states"
			class="country-button"
			type="button"
			role="combobox"
			aria-controls="dropdown-countries"
			aria-expanded="false"
			aria-haspopup="false"
			on:click={toggleDropDown}>
			{#if selectedCountry && selectedCountry !== null}
				<div class="inline-flex items-center text-left">
					<span class="flag flag-{selectedCountry.toLowerCase()} "></span>
					{#if options.format === "national"}
						<span class=" text-gray-600 dark:text-gray-400">+{selectedCountryDialCode}</span>
					{/if}
				</div>
			{:else}
				Please select
			{/if}
			<svg
				aria-hidden="true"
				class="chevron {isOpen ? 'rotate-180' : ''}"
				fill="currentColor"
				viewBox="0 0 20 20"
				xmlns="http://www.w3.org/2000/svg">
				<path
					fill-rule="evenodd"
					d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
					clip-rule="evenodd" />
			</svg>
		</button>
		{#if isOpen}
			<div
				id="dropdown-countries"
				class="dropdown-countries absolute z-10 max-w-fit bg-white rounded divide-y divide-gray-100 shadow dark:bg-gray-700 overflow-hidden translate-y-11"
				data-popper-reference-hidden=""
				data-popper-escaped=""
				data-popper-placement="bottom"
				aria-orientation="vertical"
				aria-labelledby="country-button"
				tabindex="-1">
				<div
					class="dropdown-country-buttons text-sm text-gray-700 dark:text-gray-200 max-h-48 overflow-y-auto"
					aria-labelledby="countries-button"
					role="listbox">
					<input
						aria-autocomplete="list"
						type="text"
						class="search-input"
						bind:value={searchText}
						placeholder={searchPlaceholder}
						bind:this={searchInput} />
					{#each sortCountries(normalizedCountries, searchText) as country (country.id)}
						{@const isActive = country.iso2 == selectedCountry}
						<div id="country-{country.iso2}" role="option" aria-selected={isActive}>
							<button
								value={country.iso2}
								type="button"
								class="dropdown-country-button {isActive
									? 'bg-gray-600 dark:text-white'
									: 'dark:hover:text-white dark:text-gray-400'}"
								on:click={(e) => {
									handleSelect(country.iso2, e);
								}}>
								<div class="inline-flex items-center text-left">
									<span class="flag flag-{country.iso2.toLowerCase()} flex-shrink-0 me-3"></span>
									<span class="me-2">{country.name}</span>
									<span class="text-gray-500">+{country.dialCode}</span>
								</div>
							</button>
						</div>
					{/each}
				</div>
			</div>
		{/if}

		<TelInput
			{id}
			bind:country={selectedCountry}
			bind:value={inputVaue}
			bind:detailedValue
			bind:valid
			{options}
			{disabled}
			{required}
			{name}
			{validator}
			{validatorMessage}
			{validatorTarget}
			class="form-control"
			on:change />
	</div>
	<div id="{id}_error" class="tel-input-error"></div>
</div>

<style>
	.flex {
		display: flex;
		width: 100%;
		position: relative;
	}

	.country-button {
		height: 50px;
		border: 1px solid var(--color-border);
		border-right: none;
		display: flex;
		flex-shrink: 0;
		align-items: center;
		padding: 10px 6px;
	}

	.country-button:hover {
		background-color: rgb(229, 231, 235);
	}

	.text-left {
		text-align: left;
	}

	.items-center {
		align-items: center;
	}

	.inline-flex {
		display: inline-flex;
	}

	.flag {
		margin-right: 10px;
	}

	.chevron {
		width: 1rem;
		height: 1rem;
		margin-left: 4px;
	}

	.search-input {
		border: none;
		font-size: 0.8rem;
		height: 40px;
		width: 100%;
	}

	.search-input:focus-visible {
		border: none;
		outline: none;
	}

	.dropdown-countries {
		position: absolute;
		z-index: 10;
		top: 50px;
	}

	.dropdown-country-buttons {
		max-height: 192px;
		font-size: 14px;
		overflow-y: auto;
	}

	.dropdown-country-button {
		width: 100%;
		background-color: transparent;
		border: none;
		outline: none;
		text-align: left;
		padding: 6px 10px 2px 10px;
	}

	.dropdown-country-button:hover {
		background-color: #dedede;
	}
</style>
